import { PDFName, PDFString } from "pdf-lib";

/**
 * Widget rectangle with non-negative width/height.
 *
 * /Rect is defined as [llx lly urx ury], but the spec (PDF 32000-1, 7.9.5) only
 * requires the two opposite corners - it does NOT require the first pair to be
 * the lower-left one, and tells consumers to normalize. pdf-lib's
 * `getRectangle()` subtracts blindly (`urx - llx`, `ury - lly`), so a widget
 * authored with the corners flipped comes back with a negative width or height.
 *
 * EOIR-42B has 18 such widgets (15 check boxes, 3 radio buttons). Left unfixed,
 * the negative height makes the CSS `height` declaration invalid - the browser
 * drops it and the input falls back to its intrinsic ~13px size - and pushes the
 * overlay down by exactly one widget height, because `pageHeight - y - height`
 * adds instead of subtracting.
 */
export const getWidgetRectangle = (widget) => {
    const { x, y, width, height } = widget.getRectangle();
    return {
        x: width < 0 ? x + width : x,
        y: height < 0 ? y + height : y,
        width: Math.abs(width),
        height: Math.abs(height),
    };
};

/**
 * Extracts page index and rectangle for a field's widget.
 * pageIndex is -1 when the widget has no resolvable /P entry.
 */
export const getFieldPageAndRect = (field, pages) => {
    const widgets = field.acroField.getWidgets();
    if (widgets.length === 0) return null;

    const widget = widgets[0];
    const pageIndex = pages.findIndex((p) => p.ref === widget.P());
    const rect = getWidgetRectangle(widget);

    return { pageIndex, rect, widget };
};

/** The "/Helv 10 Tf" clause of a Default Appearance string. */
const TF_REGEX = /\/([^\s/]+)\s+(\d*\.?\d+)\s+Tf/;

/**
 * Pins a field's font size everywhere pdf-lib might read it.
 *
 * `field.setFontSize()` only rewrites the FIELD's /DA, but when it regenerates an
 * appearance pdf-lib prefers the WIDGET's: `widgetFontSize ?? fieldFontSize`
 * (`api/form/appearances.js`). That only diverges when the two live in separate
 * dictionaries - on the USCIS templates checked here every field is merged with
 * its single widget, so the field-level write already covers them. This exists
 * for the forms where they are not: a field with several widgets, or one whose
 * widget carries its own /DA.
 *
 * A widget with no /DA of its own is left alone: it already inherits the field's.
 */
export const setFieldFontSize = (field, size) => {
    try {
        field.setFontSize(size);
    } catch (e) {
        // No /DA or no "Tf" operator to rewrite; the widgets below still apply.
    }

    field.acroField.getWidgets().forEach((widget) => {
        try {
            const da = widget.dict.get(PDFName.of("DA"));
            const current = typeof da?.decodeText === "function" ? da.decodeText() : da?.asString?.();
            if (!current || !TF_REGEX.test(current)) return;

            widget.dict.set(
                PDFName.of("DA"),
                PDFString.of(current.replace(TF_REGEX, (_match, fontName) => `/${fontName} ${size} Tf`))
            );
        } catch (e) {
            // A malformed widget /DA must not abort the whole document.
        }
    });
};

/**
 * Reads a field's tooltip (/TU, the accessibility text). On USCIS forms this is
 * a rich description such as
 * "Part 9. General Eligibility and Inadmissibility Grounds. Public Charge. 66. If your answer to..."
 * which is what we mine to title each addendum section.
 */
export const getFieldTooltip = (field) => {
    try {
        const tu = field.acroField.dict.get(PDFName.of("TU"));
        // Works for both PDFString and PDFHexString.
        return typeof tu?.decodeText === "function" ? tu.decodeText() : "";
    } catch (e) {
        return "";
    }
};
